import {
  Checkbox,
  Divider,
  Grid,
  Select,
  Space,
  TextInput,
  Title,
} from '@mantine/core';
import { DatePicker } from '@mantine/dates';
import { UseFormReturnType } from '@mantine/form/lib/use-form';
import { useEffect, useState } from 'react';
import { Calendar } from 'tabler-icons-react';
import { GenderData } from '../../../../data/gender-data.array';
import { BusinessTypes } from '../../../../enums/business-type';
import { Gender } from '../../../../enums/gender-enums';
import { UpdateMerchantOnboardingFormValues } from '../../../../type/update-merchant-onboarding-form-values.types';
import PhoneCountryCodeIcon from '../../../icons/phone-country-code-icon.component';

interface UpdateRepresentativeInformationProps {
  form: UseFormReturnType<UpdateMerchantOnboardingFormValues>;
  representativeInformation: boolean;
  isNonAdminInterface?: boolean;
}
function UpdateRepresentativeInformation(
  props: UpdateRepresentativeInformationProps
) {
  const { form, representativeInformation, isNonAdminInterface } = props;
  const [copyRepInfo, setCopyRepInfo] = useState(form.values.copyRepInfo);
  const [dontUseEffect, setDontUseEffect] = useState(true);
  const [copyInfoWasClicked, setCopyInfoWasClicked] = useState(false);
  const [onInitialLoad, setOnInitialLoad] = useState(true);

  useEffect(() => {
    //stops the first call of useEffect which clears the fields
    if (dontUseEffect) {
      form.setFieldValue(
        'isEMNameDisabled',
        form.values.eMiddleName === 'N/A' || form.values.eMiddleName === '.'
      );
      form.setFieldValue(
        'isAMNameDisabled',
        form.values.aMiddleName === 'N/A' || form.values.aMiddleName === '.'
      );
      setDontUseEffect(false);
      return;
    }
    if (!copyRepInfo) {
      if (!copyInfoWasClicked) {
        return;
      }
      form.setFieldValue('eFirstName', '');
      form.setFieldValue('eMiddleName', '');
      form.setFieldValue('isEMNameDisabled', false);
      form.setFieldValue('eLastName', '');
      form.setFieldValue('ePosition', '');
      form.setFieldValue('eEmailAddress', '');
      form.setFieldValue('eContactNumber', '');
      form.setFieldValue('eDateOfBirth', null);
      form.setFieldValue('eTin', '');
      form.setFieldValue('eSss', '');
    } else {
      form.setFieldValue('eFirstName', form.values.aFirstName);
      form.setFieldValue('eMiddleName', form.values.aMiddleName);
      form.setFieldValue('isEMNameDisabled', form.values.isAMNameDisabled);
      form.setFieldValue('eLastName', form.values.aLastName);
      form.setFieldValue('ePosition', form.values.aPosition);
      form.setFieldValue('eEmailAddress', form.values.aEmailAddress);
      form.setFieldValue('eContactNumber', form.values.aContactNumber);
      form.setFieldValue('eDateOfBirth', form.values.aDateOfBirth);
      form.setFieldValue('eTin', form.values.aTin);
      form.setFieldValue('eSss', form.values.aSss);
    }
    return () => {
      if (copyInfoWasClicked) {
        setCopyInfoWasClicked(false);
      }
    };
  }, [copyRepInfo]);

  useEffect(() => {
    if (
      form.values.businessTypeId !== BusinessTypes.SOLE_PROPRIETOR ||
      onInitialLoad
    ) {
      setOnInitialLoad(false);
      return;
    }
    if (!form.values.copyProToRep) {
      form.setFieldValue('aFirstName', '');
      form.setFieldValue('aMiddleName', '');
      form.setFieldValue('isAMNameDisabled', false);
      form.setFieldValue('aLastName', '');
      form.setFieldValue('aPosition', '');
      form.setFieldValue('aEmailAddress', '');
      form.setFieldValue('aContactNumber', '');
      form.setFieldValue('aDateOfBirth', null);
      form.setFieldValue('aPosition', form.values.aPosition);
      form.setFieldValue('aTin', form.values.aTin);
      form.setFieldValue('aSss', form.values.aSss);

      //if copyRepInfo is clicked and copyProToRep was unclicked
      if (form.values.copyRepInfo) {
        form.setFieldValue('eFirstName', '');
        form.setFieldValue('eMiddleName', '');
        form.setFieldValue('isEMNameDisabled', false);
        form.setFieldValue('eLastName', '');
        form.setFieldValue('ePosition', '');
        form.setFieldValue('eEmailAddress', '');
        form.setFieldValue('eContactNumber', '');
        form.setFieldValue('eDateOfBirth', null);
        form.setFieldValue('ePosition', form.values.aPosition);
        form.setFieldValue('eTin', form.values.aTin);
        form.setFieldValue('eSss', form.values.aSss);
      }
    } else if (
      form.values.incorporators &&
      form.values.incorporators.length > 0
    ) {
      const {
        firstName,
        middleName,
        hasMiddleName,
        lastName,
        emailAddress,
        contactNumber,
        birthDate,
      } = form.values.incorporators[0];
      form.setFieldValue('aFirstName', firstName);
      form.setFieldValue('aMiddleName', middleName);
      form.setFieldValue('isAMNameDisabled', hasMiddleName);
      form.setFieldValue('aLastName', lastName);
      form.setFieldValue('aPosition', form.values.aPosition);
      form.setFieldValue('aEmailAddress', emailAddress);
      form.setFieldValue('aContactNumber', contactNumber);
      form.setFieldValue('aDateOfBirth', birthDate);
      form.setFieldValue('aPosition', form.values.aPosition);
      form.setFieldValue('aTin', form.values.aTin);
      form.setFieldValue('aSss', form.values.aSss);

      //if copyRepInfo is clicked and copyProToRep was clicked
      if (form.values.copyRepInfo) {
        form.setFieldValue('eFirstName', firstName);
        form.setFieldValue('eMiddleName', middleName);
        form.setFieldValue('isEMNameDisabled', hasMiddleName);
        form.setFieldValue('eLastName', lastName);
        form.setFieldValue('ePosition', form.values.aPosition);
        form.setFieldValue('eEmailAddress', emailAddress);
        form.setFieldValue('eContactNumber', contactNumber);
        form.setFieldValue('eDateOfBirth', birthDate);
        form.setFieldValue('ePosition', form.values.aPosition);
        form.setFieldValue('eTin', form.values.aTin);
        form.setFieldValue('eSss', form.values.aSss);
      }
    }
  }, [form.values.copyProToRep]);

  const label = {
    firstName: 'First name',
    middleName: 'Middle name',
    lastName: 'Last name',
    position: 'Position',
    emailAddress: 'Email address',
    contactNumber: 'Contact number',
    dateOfBirth: 'Birthdate',
    tin: 'TIN',
    sss: 'SSS',

    aMothersMaidenName: "Mother's maiden name",
    aGender: 'Gender',
    aAubNationalityCode: 'AUB nationality code',
    aAubDeviceId: 'AUB device ID',
    aAubIdCode: 'AUB ID code',
    aAubIdNumber: 'AUB ID number',
    aAubRegionCode: 'AUB region code',

    copyProToRep: 'Use same details as the proprietor',
    copyRepInfo: 'Use same details as the authorized representative',
  };
  const placeHolder = {
    firstName: 'First name',
    middleName: 'Middle name',
    lastName: 'Last name',
    position: 'Position',
    emailAddress: 'email@email.com',
    contactNumber: 'Contact number',
    dateOfBirth: 'Select Birthdate',
    tin: 'TIN',
    sss: 'SSS',

    aMothersMaidenName: "Mother's maiden name",
    aGender: 'Gender',
    aAubNationalityCode: 'AUB nationality code',
    aAubDeviceId: 'AUB device ID',
    aAubIdCode: 'AUB ID code',
    aAubIdNumber: 'AUB ID number',
    aAubRegionCode: 'AUB region code',

    copyProToRep: 'Use same details as the proprietor',
    copyRepInfo: 'Use same details as the authorized representative',
  };

  const copyTextToField = (
    copyToField: keyof typeof form.values,
    value: string
  ) => {
    form.setFieldValue(copyToField, value);
  };
  const copyBooleanTofield = (
    copyToField: keyof typeof form.values,
    value: boolean
  ) => {
    form.setFieldValue(copyToField, value);
  };
  const copyDateToField = (
    copyToField: keyof typeof form.values,
    value: Date
  ) => {
    form.setFieldValue(copyToField, value);
  };
  return (
    <>
      <Grid
        data-cy={
          representativeInformation ? 'authorizedRegion' : 'escalationRegion'
        }
      >
        <Grid.Col>
          <Title order={4}>
            {representativeInformation
              ? "Representative's Information"
              : 'Escalation Representative'}
          </Title>
        </Grid.Col>
        {representativeInformation &&
          form.values.businessTypeId === BusinessTypes.SOLE_PROPRIETOR &&
          form.values.incorporators &&
          form.values.incorporators.length > 0 && (
            <Grid.Col>
              <Checkbox
                label={label.copyProToRep}
                {...form.getInputProps('copyProToRep', {
                  type: 'checkbox',
                })}
              />
            </Grid.Col>
          )}
        {!representativeInformation && (
          <Grid.Col>
            <Checkbox
              checked={form.values.copyRepInfo}
              onChange={(event) => {
                setCopyRepInfo(event.currentTarget.checked);
                form.setFieldValue('copyRepInfo', event.currentTarget.checked);
                setCopyInfoWasClicked(true);
              }}
              label={label.copyRepInfo}
              data-cy='copyRepInfo'
            />
          </Grid.Col>
        )}
        {((representativeInformation &&
          !(
            form.values.copyProToRep &&
            form.values.businessTypeId === BusinessTypes.SOLE_PROPRIETOR
          )) ||
          (!representativeInformation && !copyRepInfo)) && (
          <>
            <Grid.Col>
              <TextInput
                placeholder={placeHolder.firstName}
                label={label.firstName}
                value={
                  representativeInformation
                    ? form.values.aFirstName
                    : form.values.eFirstName
                }
                onChange={(event) => {
                  form.setFieldValue(
                    representativeInformation ? 'aFirstName' : 'eFirstName',
                    event.currentTarget.value
                  );
                  if (representativeInformation && form.values.copyRepInfo) {
                    copyTextToField('eFirstName', event.currentTarget.value);
                  }
                }}
                error={
                  representativeInformation
                    ? form.errors.aFirstName
                    : form.errors.eFirstName
                }
                data-cy='firstName'
              />
            </Grid.Col>
            <Grid.Col>
              <TextInput
                placeholder={placeHolder.middleName}
                label={label.middleName}
                disabled={
                  representativeInformation
                    ? form.values.isAMNameDisabled
                    : form.values.isEMNameDisabled
                }
                value={
                  representativeInformation
                    ? form.values.aMiddleName
                    : form.values.eMiddleName
                }
                onChange={(event) => {
                  form.setFieldValue(
                    representativeInformation ? 'aMiddleName' : 'eMiddleName',
                    event.currentTarget.value
                  );
                  if (representativeInformation && form.values.copyRepInfo) {
                    copyTextToField('eMiddleName', event.currentTarget.value);
                  }
                }}
                error={
                  representativeInformation
                    ? form.errors.aMiddleName
                    : form.errors.eMiddleName
                }
                data-cy='middleName'
              />
              <Space my='xs' />
              <Checkbox
                label="I don't have a middle name"
                checked={
                  representativeInformation
                    ? form.values.isAMNameDisabled
                    : form.values.isEMNameDisabled
                }
                onChange={() => {
                  let value = representativeInformation
                    ? !form.values.isAMNameDisabled
                    : !form.values.isEMNameDisabled;
                  form.setFieldValue(
                    representativeInformation
                      ? 'isAMNameDisabled'
                      : 'isEMNameDisabled',
                    value
                  );
                  form.setFieldValue(
                    representativeInformation ? 'aMiddleName' : 'eMiddleName',
                    !value ? '' : '.'
                  );
                  if (representativeInformation && form.values.copyRepInfo) {
                    copyBooleanTofield('isEMNameDisabled', value);
                    copyTextToField('eMiddleName', !value ? '' : '.');
                  }
                }}
                data-cy='noMiddleName'
              />
            </Grid.Col>
            <Grid.Col>
              <TextInput
                placeholder={placeHolder.lastName}
                label={label.lastName}
                value={
                  representativeInformation
                    ? form.values.aLastName
                    : form.values.eLastName
                }
                onChange={(event) => {
                  form.setFieldValue(
                    representativeInformation ? 'aLastName' : 'eLastName',
                    event.currentTarget.value
                  );
                  if (representativeInformation && form.values.copyRepInfo) {
                    copyTextToField('eLastName', event.currentTarget.value);
                  }
                }}
                error={
                  representativeInformation
                    ? form.errors.aLastName
                    : form.errors.eLastName
                }
                data-cy='lastName'
              />
            </Grid.Col>
            <Grid.Col>
              <TextInput
                placeholder={placeHolder.position}
                label={label.position}
                value={
                  representativeInformation
                    ? form.values.aPosition
                    : form.values.ePosition
                }
                onChange={(event) => {
                  form.setFieldValue(
                    representativeInformation ? 'aPosition' : 'ePosition',
                    event.currentTarget.value
                  );
                  if (representativeInformation && form.values.copyRepInfo) {
                    copyTextToField('ePosition', event.currentTarget.value);
                  }
                }}
                data-cy='position'
              />
            </Grid.Col>
            <Grid.Col>
              <TextInput
                placeholder={placeHolder.emailAddress}
                label={label.emailAddress}
                value={
                  representativeInformation
                    ? form.values.aEmailAddress
                    : form.values.eEmailAddress
                }
                onChange={(event) => {
                  form.setFieldValue(
                    representativeInformation
                      ? 'aEmailAddress'
                      : 'eEmailAddress',
                    event.currentTarget.value
                  );
                  if (representativeInformation && form.values.copyRepInfo) {
                    copyTextToField('eEmailAddress', event.currentTarget.value);
                  }
                }}
                error={
                  representativeInformation
                    ? form.errors.aEmailAddress
                    : form.errors.eEmailAddress
                }
                data-cy='emailAddress'
              />
            </Grid.Col>
            <Grid.Col xs={12} sm={6}>
              <TextInput
                label={label.contactNumber}
                value={
                  representativeInformation
                    ? form.values.aContactNumber
                    : form.values.eContactNumber
                }
                onChange={(event) => {
                  form.setFieldValue(
                    representativeInformation
                      ? 'aContactNumber'
                      : 'eContactNumber',
                    event.currentTarget.value
                  );
                  if (representativeInformation && form.values.copyRepInfo) {
                    copyTextToField(
                      'eContactNumber',
                      event.currentTarget.value
                    );
                  }
                }}
                error={
                  representativeInformation
                    ? form.errors.aContactNumber
                    : form.errors.eContactNumber
                }
                icon={<PhoneCountryCodeIcon />}
                iconWidth={48}
                maxLength={30}
                data-cy='contactNumber'
              />
            </Grid.Col>
            <Grid.Col xs={12} sm={6}>
              <DatePicker
                icon={<Calendar />}
                placeholder={placeHolder.dateOfBirth}
                label={label.dateOfBirth}
                value={
                  representativeInformation
                    ? form.values.aDateOfBirth
                    : form.values.eDateOfBirth
                }
                onChange={(value: Date) => {
                  form.setFieldValue(
                    representativeInformation ? 'aDateOfBirth' : 'eDateOfBirth',
                    value
                  );
                  if (representativeInformation && form.values.copyRepInfo) {
                    copyDateToField('eDateOfBirth', value);
                  }
                }}
                error={
                  representativeInformation
                    ? form.errors.aDateOfBirth
                    : form.errors.eDateOfBirth
                }
                data-cy={
                  representativeInformation ? 'aDateOfBirth' : 'eDateOfBirth'
                }
              />
            </Grid.Col>
            {!isNonAdminInterface && (
              <>
                <Grid.Col xs={12} sm={6}>
                  <TextInput
                    placeholder={placeHolder.tin}
                    label={label.tin}
                    value={
                      representativeInformation
                        ? form.values.aTin
                        : form.values.eTin
                    }
                    onChange={(event) => {
                      form.setFieldValue(
                        representativeInformation ? 'aTin' : 'eTin',
                        event.currentTarget.value
                      );
                      if (
                        representativeInformation &&
                        form.values.copyRepInfo
                      ) {
                        copyTextToField('eTin', event.currentTarget.value);
                      }
                    }}
                    error={
                      representativeInformation
                        ? form.errors.aTin
                        : form.errors.eTin
                    }
                    data-cy='tin'
                  />
                </Grid.Col>
                <Grid.Col xs={12} sm={6}>
                  <TextInput
                    placeholder={placeHolder.sss}
                    label={label.sss}
                    value={
                      representativeInformation
                        ? form.values.aSss
                        : form.values.eSss
                    }
                    onChange={(event) => {
                      form.setFieldValue(
                        representativeInformation ? 'aSss' : 'eSss',
                        event.currentTarget.value
                      );
                      if (
                        representativeInformation &&
                        form.values.copyRepInfo
                      ) {
                        copyTextToField('eSss', event.currentTarget.value);
                      }
                    }}
                    error={
                      representativeInformation
                        ? form.errors.aSss
                        : form.errors.eSss
                    }
                    data-cy='sss'
                  />
                </Grid.Col>
              </>
            )}
          </>
        )}
        {form.values.copyProToRep && representativeInformation && (
          <>
            <Grid.Col>
              <TextInput
                placeholder={placeHolder.position}
                label={label.position}
                value={
                  representativeInformation
                    ? form.values.aPosition
                    : form.values.ePosition
                }
                onChange={(event) => {
                  form.setFieldValue(
                    representativeInformation ? 'aPosition' : 'ePosition',
                    event.currentTarget.value
                  );
                  if (representativeInformation && form.values.copyRepInfo) {
                    copyTextToField('ePosition', event.currentTarget.value);
                  }
                }}
                error={
                  representativeInformation
                    ? form.errors.aPosition
                    : form.errors.ePosition
                }
                data-cy='position'
              />
            </Grid.Col>
            <Grid.Col xs={12} sm={6}>
              <TextInput
                placeholder={placeHolder.tin}
                label={label.tin}
                value={
                  representativeInformation
                    ? form.values.aTin
                    : form.values.eTin
                }
                onChange={(event) => {
                  form.setFieldValue(
                    representativeInformation ? 'aTin' : 'eTin',
                    event.currentTarget.value
                  );
                  if (representativeInformation && form.values.copyRepInfo) {
                    copyTextToField('eTin', event.currentTarget.value);
                  }
                }}
                error={
                  representativeInformation
                    ? form.errors.aTin
                    : form.errors.eTin
                }
                data-cy='tin'
              />
            </Grid.Col>
            <Grid.Col xs={12} sm={6}>
              <TextInput
                placeholder={placeHolder.sss}
                label={label.sss}
                value={
                  representativeInformation
                    ? form.values.aSss
                    : form.values.eSss
                }
                onChange={(event) => {
                  form.setFieldValue(
                    representativeInformation ? 'aSss' : 'eSss',
                    event.currentTarget.value
                  );
                  if (representativeInformation && form.values.copyRepInfo) {
                    copyTextToField('eSss', event.currentTarget.value);
                  }
                }}
                error={
                  representativeInformation
                    ? form.errors.aSss
                    : form.errors.eSss
                }
                data-cy='sss'
              />
            </Grid.Col>
          </>
        )}
        {representativeInformation && (
          <>
            <Grid.Col>
              <TextInput
                placeholder={placeHolder.aMothersMaidenName}
                label={label.aMothersMaidenName}
                value={form.values.aMothersMaidenName}
                onChange={(event) => {
                  form.setFieldValue(
                    'aMothersMaidenName',
                    event.currentTarget.value
                  );
                }}
                error={form.errors.aMothersMaidenName}
              />
            </Grid.Col>
            <Grid.Col>
              <Select
                placeholder={placeHolder.aGender}
                label={label.aGender}
                value={form.values.aGender}
                data={GenderData}
                onChange={(value: Gender) => {
                  form.setFieldValue('aGender', value);
                }}
                error={form.errors.aGender}
              />
            </Grid.Col>
            <Grid.Col>
              <TextInput
                placeholder={placeHolder.aAubNationalityCode}
                label={label.aAubNationalityCode}
                value={form.values.aAubNationalityCode}
                onChange={(event) => {
                  form.setFieldValue(
                    'aAubNationalityCode',
                    event.currentTarget.value
                  );
                }}
                error={form.errors.aAubNationalityCode}
              />
            </Grid.Col>
            <Grid.Col>
              <TextInput
                placeholder={placeHolder.aAubDeviceId}
                label={label.aAubDeviceId}
                value={form.values.aAubDeviceId}
                onChange={(event) => {
                  form.setFieldValue('aAubDeviceId', event.currentTarget.value);
                }}
                error={form.errors.aAubDeviceId}
              />
            </Grid.Col>
            <Grid.Col>
              <TextInput
                placeholder={placeHolder.aAubIdCode}
                label={label.aAubIdCode}
                value={form.values.aAubIdCode}
                onChange={(event) => {
                  form.setFieldValue('aAubIdCode', event.currentTarget.value);
                }}
                error={form.errors.aAubIdCode}
              />
            </Grid.Col>
            <Grid.Col>
              <TextInput
                placeholder={placeHolder.aAubIdNumber}
                label={label.aAubIdNumber}
                value={form.values.aAubIdNumber}
                onChange={(event) => {
                  form.setFieldValue('aAubIdNumber', event.currentTarget.value);
                }}
                error={form.errors.aAubIdNumber}
              />
            </Grid.Col>
            <Grid.Col>
              <TextInput
                placeholder={placeHolder.aAubRegionCode}
                label={label.aAubRegionCode}
                value={form.values.aAubRegionCode}
                onChange={(event) => {
                  form.setFieldValue(
                    'aAubRegionCode',
                    event.currentTarget.value
                  );
                }}
                error={form.errors.aAubRegionCode}
              />
            </Grid.Col>
          </>
        )}
        <Grid.Col>
          <Divider my='md' />
        </Grid.Col>
      </Grid>
    </>
  );
}

export default UpdateRepresentativeInformation;
